/**
 * The board view of the graph window (Miro's): every item a plain element placed in board
 * units inside one layer that pans and zooms them all, frames under them, arrows between them,
 * and over the layer the selection's handles, the style bar and the tools. Only what is on
 * screen is drawn, so big boards stay smooth.
 *
 * The board's state lives in its service (`state/board/`): this view holds only what a gesture
 * needs while it lasts (`gestures.ts`), the item whose words are being written, and the
 * clipboard listeners (`paste.ts`). Pictures come from the project's files: dragged from the
 * Assets drawer (`ASSET_DROP`), dropped from the computer, pasted, or picked with the image tool.
 *
 * Where the board makes screens (UI/UX), a frame's title says its device, a component is marked
 * ◇, and prototype mode draws where each click leads (`Interactions.tsx`).
 *
 * A picture comes in at its own proportions (at most {@link PICTURE_SIDE} on its longer side); a
 * double click on one crops it (the whole picture shown faded around the part kept, its handles
 * `data-crop-handle`; Enter or a click elsewhere keeps it, Escape leaves it as it was).
 *
 * A picture of the board (the selection, else everything) is drawn from the same elements: all
 * of them, out of sight at full size, then made a PNG by `html-to-image` (`picture.ts`).
 */
import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { useApp } from "../../../app/context";
import { ASSET_DROP, assetStem, type AssetDrop } from "../../../shared/assets";
import { toast } from "../../../shared/toast";
import { DEVICES } from "../../../shared/elements";
import { defaultSize, frameAround, HANDLES, parseCrop, smoothPath, type BoardFrame, type Point, type Rect } from "../../../state/board";
import { itemShape } from "../../../shared/elements";
import { pictureOf } from "../../../shared/assets";
import { Arrows } from "./Arrows";
import { BoardItem } from "./BoardItem";
import { Editable } from "./Editable";
import { createGestures } from "./gestures";
import { Handles } from "./Handles";
import { Interactions } from "./Interactions";
import { frameCss, placeCss } from "./look";
import { listenClipboard } from "../paste";
import { StyleBar } from "./StyleBar";
import { exportArea, savePicture, type ExportArea } from "./picture";
import { ToolBar } from "./ToolBar";

/** How far beyond the screen (pixels) items are still drawn, so a pan never shows them late. */
const MARGIN_PX = 200;

/** The longest side (board units) a picture comes in with; smaller ones keep their own size. */
export const PICTURE_SIDE = 480;

/** The size a picture at `src` comes in with: its own proportions, at most {@link PICTURE_SIDE} on its longer side. */
async function pictureSize(src: string): Promise<{ w: number; h: number }> {
  try {
    const img = new Image();
    img.src = src;
    await img.decode();
    const [w, h] = [img.naturalWidth, img.naturalHeight];
    if (!w || !h) return defaultSize("image");
    const scale = Math.min(1, PICTURE_SIDE / Math.max(w, h));
    return { w: Math.max(16, Math.round(w * scale)), h: Math.max(16, Math.round(h * scale)) };
  } catch {
    return defaultSize("image");
  }
}

/** A rectangle's place in the layer (board units). */
const rectCss = (r: Rect): JSX.CSSProperties => ({ left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px` });

/** One frame: its rectangle and its title (`titlePx`: the title's size in board units). */
function Frame(props: { frame: BoardFrame; selected: boolean; titlePx: number; editing: boolean; done: () => void }): JSX.Element {
  const { board } = useApp();
  const f = () => props.frame;
  const id = () => f().id;
  return (
    <div
      class="board-frame"
      classList={{ selected: props.selected, component: f().group.style?.component === "yes", screen: board.prototypes() && f().depth === 0 }}
      data-frame={id()}
      data-device={f().group.style?.device}
      style={{ ...placeCss(f().place), ...frameCss(f().group.style ?? {}) }}
    >
      <div class="board-frame-title" data-frame-title={id()} style={{ "font-size": `${props.titlePx}px` }}>
        <Show when={f().group.style?.component === "yes"}>
          <span class="board-frame-mark" title="A component: its instances follow it">◇</span>
        </Show>
        <Show when={board.prototyping() && board.screens()[0] && (board.screens().find((x) => x.group.style?.start === "yes") ?? board.screens()[0])!.id === id()}>
          <span class="board-start" id="board-start-flag" title="The prototype starts here">
            ▶ Start
          </span>
        </Show>
        <Editable text={f().group.name} editing={props.editing} done={props.done} write={(text) => void board.setText(id(), text)} />
        <Show when={board.prototypes() ? DEVICES[f().group.style?.device ?? ""] : undefined}>
          {(d) => (
            <span class="board-frame-device">
              {d().label} {f().place.w}×{f().place.h}
            </span>
          )}
        </Show>
      </div>
    </div>
  );
}

/** The board. `provideCancel` hands the graph window what Escape calls. */
export function BoardView(props: { provideCancel: (cancel: () => boolean) => void }): JSX.Element {
  const { board, view, actions, access, assets, presence, graph, menu } = useApp();
  let host!: HTMLDivElement;
  let picker!: HTMLInputElement;
  /** The item (or frame) whose words are being written; `<item>::<part>`: an instance part's own words. */
  const [editing, setEditing] = createSignal<string | null>(null);
  /** Where the words being written were clicked (page point; `null`: a double click, every word selected). */
  const [editAt, setEditAt] = createSignal<Point | null>(null);
  /** Starts writing `id`'s words, the caret at page point `at` (none: every word selected). */
  const write = (id: string, at: Point | null = null) => {
    setEditAt(at);
    setEditing(id);
  };
  /** Where the pointer was last (screen pixels of the area), for pastes. */
  let pointer: Point | null = null;
  /** Where the image tool's pictures go once picked. */
  let pictureAt: Point = { x: 0, y: 0 };
  const canEdit = () => access.can("editor");
  /** What a picture being made holds, drawn out of sight (`null`: none is). */
  const [exporting, setExporting] = createSignal<ExportArea | null>(null);
  let exportHost: HTMLDivElement | undefined;

  /** A mouse event in pixels from the area's corner. */
  const point = (e: MouseEvent): Point => {
    const r = host.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const gestures = createGestures(
    {
      board,
      selectLink: view.selectLink,
      removeLink: (link) => void actions.removeLink(link).then((ok) => ok && toast("Arrow removed. Press Ctrl+Z to undo.")),
      arrowAt: (index) => board.arrows()[index],
      canEdit,
      pickPictures(at) {
        pictureAt = at;
        picker.click();
      },
      edit: (id, at) => write(id, at ?? null),
      writable(id) {
        const node = graph.state.graph.nodes.find((n) => n.id === id);
        return !!node && !["image", "instance"].includes(itemShape(node.kind));
      },
      pictureRatio() {
        const ids = board.selected();
        const node = ids.length === 1 ? graph.state.graph.nodes.find((n) => n.id === ids[0]) : undefined;
        if (!node || itemShape(node.kind) !== "image") return null;
        const img = host.querySelector<HTMLImageElement>(`[data-item="${CSS.escape(node.id)}"] img`);
        const crop = parseCrop(node.style?.crop) ?? { x: 0, y: 0, w: 1, h: 1 };
        if (img?.naturalWidth && img.naturalHeight) return (img.naturalWidth * crop.w) / (img.naturalHeight * crop.h);
        const place = board.content().places.get(node.id);
        return place ? place.w / place.h : null;
      },
    },
    point,
  );
  props.provideCancel(() => {
    if (board.cropping()) return void board.endCrop(false), true;
    if (editing()) return false;
    return gestures.cancel();
  });

  /** Keeps `files` as project files and puts each on the board as a picture, from `at` along. */
  const placePictures = async (files: File[], at: Point) => {
    if (!canEdit()) return void toast("Editors and owners add pictures to the board.", "error");
    // Each at its own proportions, read from the file picked before it is sent.
    const sizes = await Promise.all(
      files.map(async (file) => {
        const url = URL.createObjectURL(file);
        try {
          return await pictureSize(url);
        } finally {
          URL.revokeObjectURL(url);
        }
      }),
    );
    const kept = await assets.upload("project", files, "");
    let x = at.x;
    for (const file of kept) {
      const size = sizes[files.findIndex((f) => file.path.endsWith(f.name))] ?? sizes[0] ?? defaultSize("image");
      await board.create("image", { x, y: at.y - size.h / 2, ...size }, { name: assetStem(file.path), attachments: [file.path] });
      x += size.w + 24;
    }
  };

  /** What is on screen (with a margin), by id, in drawing order. */
  const shown = createMemo(() => {
    const c = board.camera();
    const r = host?.getBoundingClientRect();
    const [w, h] = [r?.width || 2000, r?.height || 2000];
    const left = (-MARGIN_PX - c.x) / c.k;
    const top = (-MARGIN_PX - c.y) / c.k;
    const right = (w + MARGIN_PX - c.x) / c.k;
    const bottom = (h + MARGIN_PX - c.y) / c.k;
    const content = board.content();
    // A turned item may reach past its rectangle: count its diagonal.
    const visible = (p: { x: number; y: number; w: number; h: number; rotate?: number }) => {
      const pad = p.rotate ? Math.max(p.w, p.h) / 2 : 0;
      return p.x - pad < right && p.x + p.w + pad > left && p.y - pad < bottom && p.y + p.h + pad > top;
    };
    return { items: content.items.filter((i) => visible(i.place)).map((i) => i.id), frames: content.frames.filter((f) => visible(f.place)).map((f) => f.id) };
  });
  const itemById = createMemo(() => new Map(board.content().items.map((i) => [i.id, i])));
  const frameById = createMemo(() => new Map(board.content().frames.map((f) => [f.id, f])));
  const selected = createMemo(() => new Set(board.selected()));

  onMount(() => {
    const resize = () => {
      const r = host.getBoundingClientRect();
      board.setSize(r.width, r.height);
      if (!board.framed && r.width > 0 && graph.state.loaded) board.fit();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();
    /** Space held pans with the left button. */
    const key = (e: KeyboardEvent) => {
      if (e.type === "keydown" && e.key === "Enter" && board.cropping()) return void (e.preventDefault(), board.endCrop(true));
      if (e.key === " " && !(document.activeElement instanceof HTMLInputElement) && !(document.activeElement as HTMLElement | null)?.isContentEditable) gestures.setSpace(e.type === "keydown");
    };
    addEventListener("keydown", key);
    addEventListener("keyup", key);
    /** Where pasted things go (the pointer, else the middle of the view). */
    const pasteAt = () => {
      const r = host.getBoundingClientRect();
      const p = pointer ?? { x: r.width / 2, y: r.height / 2 };
      return board.toWorld(p.x, p.y);
    };
    const stopClipboard = listenClipboard({
      active: () => view.mode() === "board" && canEdit(),
      copy: () => board.copy(),
      // A cut frame goes with what it holds.
      remove: () => void board.removeSelection(),
      // Not nodes: the words as a note.
      paste: (text) =>
        void board.paste(text, pasteAt()).then((pasted) => {
          if (!pasted && text.trim()) void board.create("note", { ...pasteAt(), w: 0, h: 0 }, { name: text.trim().slice(0, 2000) });
        }),
      pictures: (files) => void placePictures(files, pasteAt()),
    });
    /** A file dragged from the drawer: a picture item where it was dropped (a computer's file copied into the project first). */
    const fromDrawer = async (e: Event) => {
      const drop = (e as CustomEvent<AssetDrop>).detail;
      if (!canEdit()) return void toast("Editors and owners add pictures to the board.", "error");
      const at = point({ clientX: drop.x, clientY: drop.y } as MouseEvent);
      const path = drop.source === "project" ? drop.path : (await assets.copy(drop.source, drop.path, "project", ""))?.path;
      if (!path) return;
      const w = board.toWorld(at.x, at.y);
      const read = await assets.read("project", path, true);
      const url = read ? URL.createObjectURL(new Blob([read.bytes as BlobPart], { type: read.info.mime })) : null;
      const size = url ? await pictureSize(url) : defaultSize("image");
      if (url) URL.revokeObjectURL(url);
      void board.create("image", { x: w.x - size.w / 2, y: w.y - size.h / 2, ...size }, { name: assetStem(path), attachments: [path] });
    };
    host.addEventListener(ASSET_DROP, fromDrawer);
    board.setExporter(async (what) => {
      const area = exportArea(board.content(), what);
      if (!area) return;
      setExporting(area);
      try {
        const name = board.selected().length ? "selection.png" : "board.png";
        if (exportHost) await savePicture(exportHost, area, getComputedStyle(host).backgroundColor, name);
        toast(`Saved ${name}.`);
      } catch (e) {
        toast(`The picture could not be made: ${e instanceof Error ? e.message : String(e)}`, "error");
      } finally {
        setExporting(null);
      }
    });
    onCleanup(() => {
      board.setExporter(null);
      observer.disconnect();
      removeEventListener("keydown", key);
      removeEventListener("keyup", key);
      stopClipboard();
      host.removeEventListener(ASSET_DROP, fromDrawer);
    });
  });
  // The graph arriving after the board was first shown: frame it once.
  createEffect(() => {
    if (graph.state.loaded && !board.framed && board.content().places.size) board.fit();
  });

  /**
   * The element under the pointer. Not the event's target: a press captures the pointer for the
   * board, so the browser sends the clicks that follow to the board itself.
   */
  const under = (e: MouseEvent): Element => document.elementFromPoint(e.clientX, e.clientY) ?? (e.target as Element);

  /** Double click: writes the words of the item or frame under the pointer. */
  const onDoubleClick = (e: MouseEvent) => {
    if (!canEdit()) return;
    const el = under(e);
    const id = el.closest<HTMLElement>("[data-item]")?.dataset.item ?? el.closest<HTMLElement>("[data-frame-title]")?.dataset.frameTitle;
    if (id) {
      const node = graph.state.graph.nodes.find((n) => n.id === id);
      // A picture: cropped (Miro's double click).
      if (node && /image/.test(node.kind)) return void board.startCrop(id);
      // An instance: the words of the part under the pointer (its component's words stay).
      if (node && /instance/.test(node.kind)) {
        const part = el.closest<HTMLElement>("[data-instance-part]")?.dataset.instancePart;
        const words = part && graph.state.graph.nodes.find((n) => n.id === part);
        if (words && !/image|instance/.test(words.kind)) write(`${id}::${part}`);
        return;
      }
      return void write(id);
    }
    // On the background, a sticky note right there (Miro's double click).
    const p = point(e);
    void board.create("note", { ...board.toWorld(p.x, p.y), w: 0, h: 0 }).then((made) => made && write(made));
  };

  /** Right click: selects what is under the pointer (an item, a frame by its title or its background) and offers what can be done with it. */
  const openMenu = (e: MouseEvent) => {
    const el = under(e);
    const p = point(e);
    const at = board.toWorld(p.x, p.y);
    const id = el.closest<HTMLElement>("[data-item]")?.dataset.item ?? el.closest<HTMLElement>("[data-frame-title]")?.dataset.frameTitle ?? frameAround(board.content(), { ...at, w: 0, h: 0 });
    if (id && !board.selected().includes(id)) board.select([id]);
    const any = board.selected().length > 0;
    const frame = board.selected().length === 1 ? board.content().frames.find((f) => f.id === board.selected()[0]) : undefined;
    const component = frame?.group.style?.component === "yes";
    const screen = !!frame && board.screens().some((s) => s.id === frame.id);
    const screens = canEdit() && board.prototypes() && frame
      ? [
          ...(component ? [{ label: "Make an instance", action: () => void board.instance(frame.id) }] : []),
          { label: component ? "Stop being a component" : "Make it a component", action: () => void board.toggleComponent() },
          ...(screen ? [{ label: "Start the prototype here", action: () => void board.makeStart() }, { label: "Play from this screen", command: "prototype.play" }] : []),
        ]
      : [];
    const entries = canEdit()
      ? [
          ...screens,
          { label: "Sticky note here", action: () => void board.create("note", { ...at, w: 0, h: 0 }).then((made) => made && write(made)) },
          { label: board.prototypes() ? "Screen here" : "Frame here", action: () => void board.create("frame", { x: at.x, y: at.y, w: 0, h: 0 }) },
          ...(any
            ? [
                { label: "Duplicate", command: "board.duplicate" },
                { label: "Group", command: "board.group", disabled: board.selected().length < 2 },
                { label: "Ungroup", command: "board.ungroup" },
                { label: "Bring to front", command: "board.front" },
                { label: "Send to back", command: "board.back" },
                { label: "Delete", command: "selection.delete", danger: true },
              ]
            : []),
        ]
      : [];
    const picture = { label: any ? "Export the selection as an image…" : "Export the board as an image…", command: "board.export" };
    menu.open(e, [...entries, picture, ...menu.general()]);
  };

  /** Other people's pointers on the board (those drawn in board units). */
  const cursors = () =>
    presence.others().flatMap((person) => {
      const c = person.cursor;
      if (!c || c.anchor || c.in_3d) return [];
      const cam = board.camera();
      return [{ person, x: c.x * cam.k + cam.x, y: c.y * cam.k + cam.y }];
    });

  const cursorOf = () => (board.tool() === "hand" ? "grab" : board.tool() === "select" ? "default" : board.tool() === "eraser" ? "cell" : "crosshair");

  return (
    <div
      ref={host}
      class="board-stage"
      id="board"
      data-board="yes"
      data-asset-target="board"
      data-tool={board.tool()}
      data-mode={board.prototyping() ? "prototype" : "design"}
      data-items={shown().items.length}
      style={{ cursor: cursorOf() }}
      onPointerDown={(e) => {
        // A press elsewhere ends the writing (sending it) and still does what it does.
        if (editing()) (document.activeElement as HTMLElement | null)?.blur();
        host.setPointerCapture?.(e.pointerId);
        gestures.down(e);
      }}
      onPointerMove={(e) => {
        pointer = point(e);
        gestures.move(e);
        const w = board.toWorld(pointer.x, pointer.y);
        presence.moveCursor({ x: w.x, y: w.y, z: 0, in_3d: false, anchor: null, ray: null });
      }}
      onPointerUp={(e) => void gestures.up(e)}
      onPointerCancel={() => gestures.cancel()}
      onPointerLeave={() => presence.moveCursor(null)}
      onWheel={(e) => gestures.wheel(e)}
      onDblClick={onDoubleClick}
      onContextMenu={openMenu}
      onDragOver={(e) => e.dataTransfer?.types.includes("Files") && e.preventDefault()}
      onDrop={(e) => {
        const files = [...(e.dataTransfer?.files ?? [])];
        if (!files.length) return;
        e.preventDefault();
        e.stopPropagation();
        const p = point(e);
        void placePictures(files, board.toWorld(p.x, p.y));
      }}
    >
      <div class="board-layer" style={{ transform: `translate(${board.camera().x}px, ${board.camera().y}px) scale(${board.camera().k})` }}>
        <For each={shown().frames}>
          {(id) => (
            <Show when={frameById().get(id)}>
              {(f) => <Frame frame={f()} selected={selected().has(id)} titlePx={Math.max(14, 14 / board.camera().k)} editing={editing() === id} done={() => setEditing(null)} />}
            </Show>
          )}
        </For>
        <Arrows />
        <For each={shown().items}>
          {(id) => (
            <Show when={itemById().get(id)}>
              {(item) => (
                <BoardItem
                  id={id}
                  node={item().node}
                  place={item().place}
                  selected={selected().has(id)}
                  editing={editing() === id}
                  editingPart={editing()?.startsWith(`${id}::`) ? editing()!.slice(id.length + 2) : null}
                  done={() => setEditing(null)}
                  editAt={editAt()}
                  hidden={board.cropping()?.id === id || gestures.erased().has(id)}
                />
              )}
            </Show>
          )}
        </For>
        <Show when={gestures.drawing()}>
          {(r) => <div class={`board-drawing tool-${board.tool()}`} style={{ left: `${Math.min(r().x, r().x + r().w)}px`, top: `${Math.min(r().y, r().y + r().h)}px`, width: `${Math.abs(r().w)}px`, height: `${Math.abs(r().h)}px` }} />}
        </Show>
        <Show when={gestures.ink()}>
          {(line) => (
            <svg class="board-ink" width="1" height="1" aria-hidden="true">
              <path id="board-ink" d={smoothPath(line())} fill="none" stroke={board.pen().colour} stroke-width={board.pen().width} stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          )}
        </Show>
        <Show when={board.cropping()}>
          {(c) => {
            const src = () => {
              const path = pictureOf(graph.state.graph.nodes.find((n) => n.id === c().id)?.attachments);
              return path ? assets.url(path) : null;
            };
            return (
              <Show when={src()}>
                {(s) => (
                  <>
                    {/* The whole picture, darkened outside the part kept, as Photoshop shows it. */}
                    <div class="board-crop-whole" style={rectCss(c().whole)}>
                      <img src={s()} alt="" draggable={false} />
                      <div class="board-crop-shade" style={rectCss({ x: c().box.x - c().whole.x, y: c().box.y - c().whole.y, w: c().box.w, h: c().box.h })} />
                    </div>
                    <div class="board-crop-box" data-crop-box="yes" id="board-crop" style={{ ...rectCss(c().box), outline: `${1 / board.camera().k}px solid #fff`, "--crop-px": `${1 / board.camera().k}px` }}>
                      <div class="board-crop-thirds" aria-hidden="true">
                        <span class="v" style={{ left: "33.333%" }} />
                        <span class="v" style={{ left: "66.667%" }} />
                        <span class="h" style={{ top: "33.333%" }} />
                        <span class="h" style={{ top: "66.667%" }} />
                      </div>
                      <For each={HANDLES}>
                        {(h) => {
                          /** Corners are L-shaped, sides short bars, all the same size on screen. */
                          const corner = h.length === 2;
                          const long = () => (corner ? 18 : 22) / board.camera().k;
                          const thick = () => 4 / board.camera().k;
                          const hit = () => 18 / board.camera().k;
                          const across = h === "n" || h === "s";
                          return (
                            <span
                              class={`board-crop-handle h-${h}`}
                              classList={{ corner }}
                              data-crop-handle={h}
                              style={{
                                left: `${h.includes("w") ? 0 : h.includes("e") ? 100 : 50}%`,
                                top: `${h.includes("n") ? 0 : h.includes("s") ? 100 : 50}%`,
                                width: `${corner ? long() : across ? long() : hit()}px`,
                                height: `${corner ? long() : across ? hit() : long()}px`,
                                margin: corner
                                  ? `${h.includes("n") ? -thick() : thick() - long()}px 0 0 ${h.includes("w") ? -thick() : thick() - long()}px`
                                  : `${-(across ? hit() : long()) / 2}px 0 0 ${-(across ? long() : hit()) / 2}px`,
                                "--crop-thick": `${thick()}px`,
                              }}
                            />
                          );
                        }}
                      </For>
                    </div>
                    {/* Apply or cancel, under the part kept, at the same size on screen whatever the zoom. */}
                    <div
                      id="board-crop-bar"
                      class="board-crop-bar"
                      style={{ left: `${c().box.x + c().box.w / 2}px`, top: `${c().box.y + c().box.h}px`, transform: `scale(${1 / board.camera().k}) translate(-50%, 12px)` }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onDblClick={(e) => e.stopPropagation()}
                    >
                      <span class="board-crop-size">
                        {Math.round(c().box.w)} × {Math.round(c().box.h)}
                      </span>
                      <button type="button" id="board-crop-reset" title="Keep the whole picture" onClick={() => board.resetCrop()}>
                        Reset
                      </button>
                      <button type="button" id="board-crop-cancel" title="Cancel (Esc)" onClick={() => void board.endCrop(false)}>
                        ✕ Cancel
                      </button>
                      <button type="button" id="board-crop-apply" class="primary" title="Apply (Enter)" onClick={() => void board.endCrop(true)}>
                        ✓ Apply
                      </button>
                    </div>
                  </>
                )}
              </Show>
            );
          }}
        </Show>
        <Show when={board.prototyping()}>
          <Interactions leading={gestures.leading()} />
        </Show>
        <Show when={gestures.linking()}>
          {(l) => {
            const from = () => board.content().places.get(l().from);
            return (
              <Show when={from()}>
                {(f) => (
                  <svg class="board-arrows" aria-hidden="true">
                    <line class="board-linking" x1={f().x + f().w / 2} y1={f().y + f().h / 2} x2={l().to.x} y2={l().to.y} />
                  </svg>
                )}
              </Show>
            );
          }}
        </Show>
      </div>
      <Handles marquee={gestures.marquee()} hidden={!canEdit() || board.prototyping() || !!board.cropping()} />
      <For each={cursors()}>
        {(c) => (
          <div class="board-cursor" data-board-cursor={c.person.id} style={{ left: `${c.x}px`, top: `${c.y}px`, color: c.person.colour }}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path d="M2 1l11 6-5 1.5L5.5 14z" fill="currentColor" stroke="#fff" stroke-width="1" />
            </svg>
            <span style={{ background: c.person.colour }}>{c.person.name}</span>
          </div>
        )}
      </For>
      <Show when={canEdit() && !board.cropping()}>
        <StyleBar />
      </Show>
      <ToolBar />
      <Show when={exporting()}>
        {(area) => (
          <div class="board-export" id="board-export" aria-hidden="true">
            <div ref={exportHost} class="board-picture" style={{ width: `${area().box.w}px`, height: `${area().box.h}px` }}>
              <div class="board-layer" style={{ transform: `translate(${-area().box.x}px, ${-area().box.y}px)` }}>
                <For each={area().frames}>{(id) => <Show when={frameById().get(id)}>{(f) => <Frame frame={f()} selected={false} titlePx={14} editing={false} done={() => {}} />}</Show>}</For>
                <Arrows only={area().ids} />
                <For each={area().items}>{(id) => <Show when={itemById().get(id)}>{(item) => <BoardItem id={id} node={item().node} place={item().place} selected={false} editing={false} done={() => {}} />}</Show>}</For>
              </div>
            </div>
          </div>
        )}
      </Show>
      <input
        ref={picker}
        type="file"
        accept="image/*"
        multiple
        hidden
        id="board-picture-input"
        onChange={(e) => {
          const files = [...(e.currentTarget.files ?? [])];
          e.currentTarget.value = "";
          board.setTool("select");
          if (files.length) void placePictures(files, pictureAt);
        }}
      />
      <Show when={graph.state.loaded && !board.content().places.size}>
        <div class="empty-graph board-empty">
          <p>The board is empty.</p>
          <Show when={board.prototypes()} fallback={<p class="muted">Pick a tool on the left (S: a sticky note, T: text, R: a shape, Shift+F: a frame), or double-click anywhere.</p>}>
            <p class="muted">Draw a screen with Shift+F (a click: a phone), put buttons (B), inputs (Shift+I) and text (T) on it, then in prototype mode (Shift+E) drag from a button to another screen. Shift+P plays it.</p>
          </Show>
        </div>
      </Show>
    </div>
  );
}
